import { styled } from '@mui/material/styles';
import DoneIcon from '@mui/icons-material/Done';
import { Typography } from '@components/Typography';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { BILLING_TIME_MAP, BILLING_TYPE_MAP, PLUSPOINT_MIN_LOCATIONS, PLUSPOINT_PLAN } from '@constants/billing';
import { formatCurrency } from '@helpers/billing';
import { useTranslation } from '@hooks';
import QuantityStepper from './QuantityStepper';

const Wrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    textAlign: 'left',
});

const PlanCard = styled('div')({
    border: '1px solid var(--primary-main)',
    backgroundColor: 'var(--secondary-light)',
    borderRadius: 8,
    padding: 16,
});

const PriceRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
});

const SavingChip = styled('span')({
    flex: 'none',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: 'var(--light-main)',
    color: 'var(--success-main)',
});

const TabLabel = styled('span')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
});

const TabChip = styled('span')({
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: 11,
    fontWeight: 500,
    backgroundColor: 'var(--secondary-light)',
    color: 'var(--success-main)',
    whiteSpace: 'nowrap',
});

const Features = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 12,
});

const Summary = styled('div')({
    backgroundColor: 'var(--action-highlight)',
    border: '1px solid var(--divider)',
    borderRadius: 8,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
});

const SummaryRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
});

const Divider = styled('hr')({
    border: 0,
    borderTop: '1px solid var(--divider)',
    margin: '8px 0',
    width: '100%',
});

const tickStyles = { width: 14, height: 14, color: 'var(--success-main)', flex: 'none' };

const SubscribeForm = ({ form }) => {
    const { t } = useTranslation();
    const {
        billingTimePlan,
        setBillingTimePlan,
        locations,
        setLocations,
        minLocations,
        users,
        setUsers,
        minUsers,
        additionalUsers,
        pricing,
        planTotal,
        usersTotal,
        directoriesTotal,
        total,
        directoriesLocation,
    } = form;

    const isAnnual = billingTimePlan === BILLING_TIME_MAP.ANNUAL;
    const period = isAnnual ? t('SettingsGeneral.year') : t('SettingsGeneral.month');
    const money = amount => formatCurrency(pricing.currency, amount);

    return (
        <Wrapper>
            <RadioButtonsGroup
                value={billingTimePlan}
                onChange={setBillingTimePlan}
                options={[
                    {
                        value: BILLING_TIME_MAP.ANNUAL,
                        label: (
                            <TabLabel>
                                {t('SettingsGeneral.annual')}
                                <TabChip>{t('SettingsGeneral.twoMonthsFree')}</TabChip>
                            </TabLabel>
                        ),
                    },
                    { value: BILLING_TIME_MAP.MONTHLY, label: t('SettingsGeneral.monthly') },
                ]}
            />

            <PlanCard>
                <PriceRow>
                    <div>
                        <Typography variant="h5" sx={{ color: 'var(--text-primary)' }}>
                            {money(pricing.price[BILLING_TYPE_MAP.PLUSPOINT])}
                            <Typography variant="caption" component="span" sx={{ color: 'var(--text-secondary)' }}>
                                {` / ${t('SettingsGeneral.perLocationPer', { period })}`}
                            </Typography>
                        </Typography>
                    </div>
                    {isAnnual ? <SavingChip>{t('SettingsGeneral.twoMonthsFree')}</SavingChip> : null}
                </PriceRow>
                <Features>
                    {[
                        t('SettingsGeneral.pluspointFeatureEverything'),
                        t('SettingsGeneral.pluspointFeatureReviews'),
                        t('SettingsGeneral.pluspointFeatureInbox'),
                        t('SettingsGeneral.pluspointFeatureEngage'),
                        t('SettingsGeneral.pluspointFeatureAllowances', {
                            users: PLUSPOINT_PLAN.users,
                            credits: PLUSPOINT_PLAN.credits,
                        }),
                    ].map(feature => (
                        <Typography
                            key={feature}
                            variant="caption"
                            sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}
                        >
                            <DoneIcon sx={tickStyles} />
                            {feature}
                        </Typography>
                    ))}
                </Features>
            </PlanCard>

            <QuantityStepper
                label={t('SettingsGeneral.numberOfCompanies')}
                caption={
                    minLocations === PLUSPOINT_MIN_LOCATIONS
                        ? t('SettingsGeneral.minimumLocations', { count: PLUSPOINT_MIN_LOCATIONS })
                        : null
                }
                value={locations}
                min={minLocations}
                onChange={setLocations}
            />
            <QuantityStepper
                label={t('SettingsGeneral.numberOfUsers')}
                caption={t('SettingsGeneral.usersIncluded', { count: PLUSPOINT_PLAN.users })}
                value={users}
                min={minUsers}
                onChange={setUsers}
            />

            <Summary>
                <SummaryRow>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {t('SettingsGeneral.summaryPlan', { count: locations })}
                    </Typography>
                    <Typography variant="subtitle2">{`${money(planTotal)} / ${period}`}</Typography>
                </SummaryRow>
                {additionalUsers > 0 ? (
                    <SummaryRow>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {t('SettingsGeneral.summaryAdditionalUsers', { count: additionalUsers })}
                        </Typography>
                        <Typography variant="subtitle2">{`${money(usersTotal)} / ${period}`}</Typography>
                    </SummaryRow>
                ) : null}
                {directoriesLocation ? (
                    <SummaryRow>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {`${t('SettingsCompanies.localDirectories')} · ${directoriesLocation.name}`}
                        </Typography>
                        <Typography variant="subtitle2">{`${money(directoriesTotal)} / ${period}`}</Typography>
                    </SummaryRow>
                ) : null}
                <Divider />
                <SummaryRow>
                    <Typography variant="subtitle2">{t('SettingsGeneral.totalPrice')}</Typography>
                    <Typography variant="subtitle2">{`${money(total)} / ${period}`}</Typography>
                </SummaryRow>
            </Summary>
        </Wrapper>
    );
};

export default SubscribeForm;
